import React, { useEffect, useRef, useState } from "react";
import {
  Flex,
  Input,
  InputGroup,
  FormLabel,
  FormErrorMessage,
  FormControl,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  InputLeftElement,
  Box,
} from "@chakra-ui/react";
import i18n from "../../i18n/i18n";
import { colors } from "../../definitions/constants/GlobalStyles";
import BodyText from "./BodyText";
import { Autocomplete, Libraries, LoadScript } from "@react-google-maps/api";
import IconArrowLinear from "../../assets/icons/IconArrowLinear";
import { InputErrorIcon } from "../../definitions/constants/IconsComponent";
import IconSearch from "../../assets/icons/IconSearch";

interface CitySelectorProps {
  label?: string;
  error?: string;
  placeholder?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  place: any;
  setPlace: (place: any) => void;
}

const libraries: Libraries = ["places"];

const CitySelector: React.FC<CitySelectorProps> = ({
  label = i18n.t("city"),
  error,
  placeholder = i18n.t("search"),
  isRequired = true,
  isInvalid = false,
  place,
  setPlace,
}) => {
  const autocompleteRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [initialSuggestions, setInitialSuggestions] = useState<any[]>([]);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const options = {
    types: ["(cities)"],
  };

  useEffect(() => {
    setInitialSuggestions([
      { description: "Ciudad de México" },
      { description: "Buenos Aires" },
      { description: "Madrid" },
      { description: "São Paulo" },
      { description: "Lima" },
    ]);
  }, []);

  const handlePlaceChanged = () => {
    const address = autocompleteRef.current.getPlace();
    if (address && address.formatted_address) {
      setPlace(address);
    }
  };

  return (
    <FormControl
      display="flex"
      flexDirection="column"
      isRequired={isRequired}
      isInvalid={!!error}
      gap={0.5}
    >
      <Flex gap={1} alignItems="center">
        <FormLabel {...formLabelStyle} requiredIndicator={null}>
          {label}
        </FormLabel>
        <BodyText
          fontSize={{ base: "12px", md: "13px" }}
          fontWeight="regular"
          textColor={colors.text.lightGray3}
        >
          {i18n.t("required")}
        </BodyText>
      </Flex>
      <Popover>
        <PopoverTrigger>
          <Button
            ref={buttonRef}
            {...buttonStyle}
            borderColor={isInvalid ? "red.500" : colors.backgrounds.gray1}
            style={{
              boxShadow: isInvalid ? "0 0 0 1px #E53E3E" : "none",
            }}
          >
            {place?.description ? place?.description : i18n.t("selectCity")}
            <IconArrowLinear
              stroke={colors.text.lightGray3}
              style={{
                transform: "rotate(90deg)",
              }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          borderRadius={16}
          width={buttonRef.current?.offsetWidth}
        >
          <PopoverBody>
            <LoadScript
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              libraries={libraries}
              language={i18n.language}
            >
              <Autocomplete
                onLoad={(autocomplete) =>
                  (autocompleteRef.current = autocomplete)
                }
                onPlaceChanged={handlePlaceChanged}
                options={options}
              >
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    <IconSearch stroke={colors.figmaColors.secColor} />
                  </InputLeftElement>
                  <Input
                    type="text"
                    {...inputStyle}
                    placeholder={placeholder}
                    defaultValue={place?.description || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setPlace(null);
                      } else {
                        setPlace({ ...place, description: value });
                      }
                    }}
                    color={colors.figmaColors.secColor}
                    borderColor={colors.backgrounds.gray1}
                  />
                </InputGroup>
              </Autocomplete>
            </LoadScript>
            <Box>
              {initialSuggestions.map((suggestion, index) => (
                <Box
                  key={index}
                  onClick={() => setPlace(suggestion)}
                  {...inputStyle}
                >
                  {suggestion.description}
                </Box>
              ))}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {error && (
        <FormErrorMessage color="red.500" mt={0} gap="4px">
          <InputErrorIcon />
          <BodyText color="red.500">{i18n.t("input_error")}</BodyText>
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default CitySelector;

const formLabelStyle = {
  fontSize: "14px",
  fontWeight: "600",
  color: colors.text.black,
  margin: 0,
  paddingLeft: 4,
};

const buttonStyle = {
  fontSize: "14px",
  fontWeight: "400",
  height: "60px",
  borderRadius: "20px",
  border: "1px",
  justifyContent: "space-between",
  backgroundColor: colors.backgrounds.white,
  color: colors.text.lightGray3,
  _hover: {
    backgroundColor: "transparent",
  },
};

const inputStyle = {
  fontSize: "14px",
  fontWeight: "400",
  borderRadius: "12px",
  _placeholder: {
    color: colors.figmaColors.secColor,
  },
};
